// SkipCustomerRevenuePage — /reports/skip-management/customers.
//
// Revenue and volume by customer. KPI cards + two bar charts (top 10 by
// revenue, top 10 by volume) + a sortable per-customer table with CSV export.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { DateRangeFilter, lastThirtyDays } from './components/DateRangeFilter';
import { KpiGrid, type KpiTile } from './components/KpiGrid';
import { BarChart } from './components/BarChart';
import { SortableTable, type SortableColumn } from './components/SortableTable';
import { gbp, formatNumber } from './components/format';
import { useCustomerRevenue, type DateRange } from './useSkipReports';
import type { ReportCell } from '../useReports';
import styles from './skipReports.module.css';

const TABLE_COLUMNS: SortableColumn[] = [
  { key: 'customer_name', label: 'Customer', type: 'string' },
  { key: 'jobs', label: 'Jobs', type: 'number' },
  { key: 'revenue', label: 'Revenue', type: 'currency' },
  { key: 'outstanding', label: 'Outstanding', type: 'currency' },
  { key: 'last_job_date', label: 'Last job', type: 'date' },
];

export function SkipCustomerRevenuePage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<DateRange>(lastThirtyDays());
  const [submitted, setSubmitted] = useState<DateRange>(form);
  const enabled = !!submitted.date_from && !!submitted.date_to;

  const query = useCustomerRevenue(submitted, enabled);
  const data = query.data ?? null;

  const kpis = useMemo<KpiTile[]>(() => {
    if (!data) return [];
    return [
      { label: 'Active customers', value: formatNumber(data.kpis.active_customers), tone: 'blue' },
      { label: 'Total revenue', value: gbp(data.kpis.total_revenue), tone: 'purple' },
      { label: 'Avg job value', value: gbp(data.kpis.avg_job_value), tone: 'green' },
      { label: 'Outstanding', value: gbp(data.kpis.outstanding), tone: 'red' },
    ];
  }, [data]);

  const tableRows = useMemo<Record<string, ReportCell>[]>(
    () =>
      (data?.customers ?? []).map((c) => ({
        customer_name: c.customer_name,
        jobs: c.jobs,
        revenue: c.revenue,
        outstanding: c.outstanding,
        last_job_date: c.last_job_date,
      })),
    [data],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Customer Revenue"
        subtitle="Revenue, volume and outstanding balances by customer"
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/reports/skip-management')}>
            Back
          </Button>
        }
      />

      <DateRangeFilter
        form={form}
        onChange={setForm}
        onSubmit={() => setSubmitted({ ...form })}
        loading={query.isFetching}
      />

      {query.isFetching && !data ? (
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      ) : null}

      {data ? <KpiGrid tiles={kpis} /> : null}

      {data ? (
        <Card title="Top customers by revenue">
          <BarChart
            data={data.top_by_revenue.map((r) => ({ label: r.customer_name, value: r.revenue }))}
            colour="#7c3aed"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Top customers by volume">
          <BarChart
            data={data.top_by_volume.map((r) => ({ label: r.customer_name, value: r.jobs }))}
            colour="var(--color-secondary, #2563eb)"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Customer breakdown" flush>
          <SortableTable
            columns={TABLE_COLUMNS}
            rows={tableRows}
            exportName="skip-customer-revenue"
            emptyMessage="No customer activity for this period."
          />
        </Card>
      ) : null}

      {!query.isFetching && !data ? (
        <Card>
          <p className={styles.empty}>
            <Users size={20} aria-hidden /> Pick a date range and refresh to see the report.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
